import { PlusIcon } from 'lucide-react'
import { Button } from '@app/components/ui/button'
import { cn } from '@app/lib/cn'
import { formatCount } from './format'

/**
 * The set's sheets — "tarama 1/2" in the module design, made clickable.
 *
 * A saya set does not fit on one scanner bed with the pieces lying apart, so a
 * revision holds several sheets and exactly one of them is on the canvas at a
 * time. The strip is the only thing that says so: without it the parts of the
 * other sheets sat in the rail with no outline to belong to.
 *
 * The number on a tab is the SHEET number. Piece numbers restart on every
 * sheet, because they are the link between the rail and the outlines drawn on
 * this one photograph — a set-wide numbering would point at shapes that are not
 * on screen.
 */

export interface SheetTab {
  scanId: number
  /** 1-based position in the set, oldest first. */
  number: number
  pieceCount: number
  /** Unsent edits or an untraced sheet — the operator still owes this one work. */
  unsaved: boolean
  /** Quality warnings, a missing trace or a missing calibration on this sheet. */
  attention: boolean
}

export function SheetStrip({
  tabs,
  activeScanId,
  onSelect,
  onAdd,
  addDisabledReason,
  progress,
}: {
  tabs: readonly SheetTab[]
  activeScanId: number | null
  onSelect: (scanId: number) => void
  /** Absent for a read-only revision — an approved set takes no more sheets. */
  onAdd?: () => void
  /** Why another sheet cannot be added right now, in Turkish, or null. */
  addDisabledReason?: string | null
  /**
   * Where a multi-file upload has got to — "Tarama 3/5 yükleniyor — arka.jpg".
   *
   * It belongs HERE, next to the tabs, because the tabs are what the queue is
   * building: each file that lands becomes the next tab, and the sentence says
   * how many are still coming so a half-built strip does not read as the set.
   */
  progress?: string | null
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tabs.map((tab) => {
        const active = tab.scanId === activeScanId
        return (
          <button
            key={tab.scanId}
            type="button"
            onClick={() => onSelect(tab.scanId)}
            aria-current={active ? 'true' : undefined}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xs border px-2 py-1 text-micro transition-colors',
              active
                ? 'border-kordovan bg-kordovan text-kagit'
                : 'border-hairline bg-kagit text-ink hover:border-ink-muted/45',
            )}
          >
            <span className="font-medium">Tarama {formatCount(tab.number)}</span>
            <span className={cn('font-mono tnum', active ? 'text-kagit' : 'text-ink-muted')}>
              {formatCount(tab.pieceCount)} parça
            </span>
            {/* Keep active attention mustard; at 2.6:1/1.3:1 on oxblood/salmon it needs the kagit ring. */}
            {tab.attention || tab.unsaved ? (
              <span
                aria-hidden
                className={cn(
                  'size-1.5 rounded-full',
                  tab.attention ? 'bg-hardal' : active ? 'bg-kagit' : 'bg-ink-muted',
                  tab.attention && active && 'ring-1 ring-kagit',
                )}
              />
            ) : null}
          </button>
        )
      })}

      {onAdd ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onAdd}
          disabled={addDisabledReason != null}
          title={addDisabledReason ?? undefined}
        >
          <PlusIcon aria-hidden />
          Tarama ekle
        </Button>
      ) : null}

      {progress ? (
        <span className="font-mono text-micro tnum text-ink-muted" aria-live="polite">
          {progress}
        </span>
      ) : null}
    </div>
  )
}
